import type { Lang } from './types';

const STRINGS: Record<string, { en: string; zh: string }> = {
  'home.hero': { en: 'See what lies beneath.', zh: '观心听澜' },
  'home.subtitle': {
    en: 'A single thought. Then the cards answer back.',
    zh: '落笔一念，静待牌意的回响',
  },
  'home.placeholder': { en: 'What are you thinking of, right now…', zh: '此刻，你在想什么…' },
  'home.begin': { en: 'Begin', zh: '开始' },
  'home.skip': { en: 'Or begin without a question — for a general reading', zh: '无问而卜' },
  'formations.title': { en: 'Choose a formation.', zh: '择一卦形' },
  'formations.general': { en: 'A general reading — no question posed.', zh: '无问之卜' },
  'breath.line': { en: 'the deep is listening…', zh: '深海正在聆听……' },
  'breath.hint_skip': { en: 'tap anywhere to continue', zh: '点按任意处以继续' },
  'reveal.drawing': { en: 'Drawing your cards…', zh: '落卦中……' },
  'reveal.complete': { en: 'The cards have spoken.', zh: '卦象既成' },
  'reveal.hint': { en: 'Tap each card to reveal', zh: '点击卡牌以翻开' },
  'reading.for_question': { en: 'for your question', zh: '针对你的问题' },
  'reading.skeleton': { en: 'Reading this card in your context…', zh: '正在为你解读此卦……' },
  'reading.ai_failed': {
    en: "The deep is quiet right now. Let the card's own meaning speak.",
    zh: '深海之声此刻未至。上方卦词可独自成读。',
  },
  'thread.title': { en: 'The thread', zh: '一线之辞' },
  'thread.loading': { en: 'Ripples settle. The mind grows clear…', zh: '水波不兴，心迹自明……' },
  'thread.weaving': { en: 'Weaving the new card in…', zh: '正在融入新卦……' },
  'thread.fallback': {
    en: 'A phase of active forward motion is gently receding, making space for your current energy of mindful caution. This intentional slowing is beautifully preparing the way for a much-needed period of serene rest and deep renewal to embrace you.',
    zh: '一段积极前行的能量正温柔地退去，为你此刻专注谨慎的能量让出空间。这份有意的缓慢，正为你即将迎来的深深休憩与更新，铺开一条静好的路。',
  },
  'thread.reflection': {
    en: 'Tonight, before you sleep, name one small thing you can let rest until morning.',
    zh: '今夜入睡前，挑一件小事，允许它停下，留待明日。',
  },
  'thread.composed_by': { en: 'Reading composed by 深海', zh: '深海所述' },
  'ai.state_static': { en: 'Static reading — the deep is silent', zh: '静读模式 · 深海未醒' },
  'actions.draw_again': { en: 'Draw again', zh: '再卜一次' },
  'actions.clarify': { en: 'Pull a clarifying card', zh: '抽一张补卦' },
  'actions.clarify_cap': { en: 'A destiny already drawn cannot be disturbed.', zh: '勿扰既定命运' },
  'actions.done': { en: "I've seen enough", zh: '心中有数了' },
  menu: { en: 'Menu', zh: '目录' },
  'position.clarifier': { en: 'Clarifying card', zh: '补卦' },
};

let currentLang: Lang = 'zh';

export function initI18n(initial?: Lang): void {
  currentLang = initial ?? 'zh';
  document.documentElement.lang = currentLang;
}

export function getLang(): Lang {
  return currentLang;
}

export function setLang(l: Lang): void {
  currentLang = l;
  document.documentElement.lang = l;
  // Notify listeners (screens that render bilingual content)
  window.dispatchEvent(new CustomEvent('linyuan:lang-change', { detail: l }));
}

export function t(key: string): string {
  const entry = STRINGS[key];
  if (!entry) throw new Error(`i18n key not found: ${key}`);
  return entry[currentLang];
}

/**
 * Per-language lookup. Use these inside `<span lang="en">…</span><span lang="zh">…</span>`
 * dual-span markup so each span contains its correct-language string. CSS hides whichever
 * lang is not active on `<html>`, so the toggle becomes a pure DOM/CSS operation.
 *
 * Use `t()` only when the active-language string is needed once (e.g. an input
 * placeholder, where the dual-span trick is not possible).
 */
export function tEn(key: string): string {
  const entry = STRINGS[key];
  if (!entry) throw new Error(`i18n key not found: ${key}`);
  return entry.en;
}

export function tZh(key: string): string {
  const entry = STRINGS[key];
  if (!entry) throw new Error(`i18n key not found: ${key}`);
  return entry.zh;
}
